import {
  Checkbox,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";

const ReusableTable = ({
  columns = [],
  data = [],
  watchedValues = {},
  editableColumns = [],
  control,
  containerClassName = "",
  tableClassName = "",
  headerClassName = "",
  rowClassName = "",
  cellClassName = "",
  isEditingEnabled = true,
  autoCalculation = null,
  enableSelection = false,
  selectedItems = [],
  onSelectionChange = null,
  onSelectAll = null,
  selectionKey = "id",
  onFieldBlur = null,
  size = "auto",
  height = null,
  maxHeight = null,
  showSelectAllInHeader = true,
  emptyStateMessage = "No data available",
  emptyStateIcon = null,
  disableRowHover = false,
  onRowClick = null,
  // Pagination props
  pagination = null,
  onPageChange = null,
  paginationLoading = false,
}) => {
  const [editingCells, setEditingCells] = useState(new Set());
  const [calculatedValues, setCalculatedValues] = useState({});

  useEffect(() => {
    if (!autoCalculation) return;

    const { sourceField, factorField, targetField } = autoCalculation;
    const newCalculatedValues = {};

    data.forEach((row, rowIndex) => {
      const sourceFieldName = `${rowIndex}_${sourceField}`;
      const sourceValue = watchedValues?.[sourceFieldName];

      if (sourceValue && row[factorField]) {
        const numericSourceValue = parseInt(sourceValue);
        const numericFactorValue = parseInt(row[factorField]);

        if (!isNaN(numericSourceValue) && !isNaN(numericFactorValue)) {
          newCalculatedValues[`${rowIndex}_${targetField}`] =
            numericSourceValue * numericFactorValue;
        }
      }
    });

    setCalculatedValues(newCalculatedValues);
  }, [watchedValues, data, autoCalculation]);

  const handleCellClick = (rowIndex, columnKey) => {
    if (isEditingEnabled && editableColumns.includes(columnKey)) {
      const cellKey = `${rowIndex}-${columnKey}`;
      setEditingCells((prev) => new Set([...prev, cellKey]));
    }
  };

  const handleCellBlur = (rowIndex, columnKey) => {
    const cellKey = `${rowIndex}-${columnKey}`;
    setEditingCells((prev) => {
      const newSet = new Set(prev);
      newSet.delete(cellKey);
      return newSet;
    });
  };

  const isTextLong = (text) => {
    if (!text || text === "-") return false;
    return text.toString().length > 30;
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text || text === "-") return text;
    const textStr = text.toString();
    if (textStr.length <= maxLength) return textStr;
    return textStr.substring(0, maxLength) + "...";
  };

  const isTextVeryLong = (text) => {
    if (!text || text === "-") return false;
    return text.toString().length > 50;
  };

  const formatTextForDisplay = (text) => {
    if (!text || text === "-") return text;
    const textStr = text.toString();

    if (textStr.length <= 50) return textStr;

    const words = textStr.split(" ");
    const lines = [];
    let currentLine = [];
    let currentLineLength = 0;
    const maxCharsPerLine = Math.ceil(textStr.length / 3);

    for (const word of words) {
      if (
        currentLineLength + word.length + 1 > maxCharsPerLine &&
        currentLine.length > 0
      ) {
        lines.push(currentLine.join(" "));
        currentLine = [word];
        currentLineLength = word.length;
      } else {
        currentLine.push(word);
        currentLineLength += word.length + (currentLine.length > 1 ? 1 : 0);
      }

      if (lines.length >= 3) break;
    }

    if (lines.length < 3 && currentLine.length > 0) {
      lines.push(currentLine.join(" "));
    }

    const totalWordsUsed = lines.reduce(
      (acc, line) => acc + line.split(" ").length,
      0
    );
    const hasMore = totalWordsUsed < words.length;

    return {
      displayText: lines.join("\n"),
      fullText: textStr,
      hasMore: hasMore,
    };
  };

  const shouldShowTooltip = (text) => {
    if (!text || text === "-") return false;
    const textStr = text.toString();
    return textStr.length > 0;
  };

  const formatNumber = (value, format) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === "-"
    ) {
      return "-";
    }

    let stringValue = String(value).trim();

    if (stringValue === "") {
      return "-";
    }

    let numericValue = stringValue.replace(/[^\d.-]/g, "");

    if (numericValue === "" || numericValue === "-") {
      return "-";
    }

    const num = parseFloat(numericValue);
    if (isNaN(num)) return "-";

    switch (format) {
      case "currency":
        return `$${num.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`;
      case "number":
        return num.toLocaleString("en-US");
      case "integer":
        return Math.round(num).toLocaleString("en-US");
      default:
        return value;
    }
  };

  const renderCell = (row, column, rowIndex) => {
    if (column.render && typeof column.render === "function") {
      const content = column.render(row, rowIndex);

      const extractText = (element) => {
        if (typeof element === "string") return element;
        if (typeof element === "number") return String(element);
        if (!element) return "";

        if (element.props) {
          if (typeof element.props.children === "string")
            return element.props.children;
          if (Array.isArray(element.props.children)) {
            return element.props.children
              .map((child) => extractText(child))
              .join(" ");
          }
          if (element.props.children?.props) {
            return extractText(element.props.children);
          }
        }

        return "";
      };

      const contentText = extractText(content);
      const isLongText = contentText && contentText.length > 100;

      if (isLongText) {
        return (
          <Tooltip
            title={contentText}
            placement="top"
            arrow
            PopperProps={{
              sx: {
                "& .MuiTooltip-tooltip": {
                  fontSize: "13px",
                  maxWidth: "400px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  backgroundColor: "#333",
                  color: "#fff",
                  padding: "8px 12px",
                  borderRadius: "6px",
                },
                "& .MuiTooltip-arrow": {
                  color: "#333",
                },
              },
            }}
          >
            <div>{content}</div>
          </Tooltip>
        );
      }

      return <div>{content}</div>;
    }

    const isEditable = editableColumns.includes(column.key);
    const disableCellHover = column.disableHover || false;
    const fieldName = `${rowIndex}_${column.key}`;
    let value = watchedValues?.[fieldName] ?? row[column.key];

    if (autoCalculation && column.key === autoCalculation.targetField) {
      const calculatedValue = calculatedValues[fieldName];
      if (calculatedValue !== undefined) {
        value = calculatedValue;
      }
    }

    if (column.format) {
      value = formatNumber(value, column.format);
    } else if (!value || value.toString().trim() === "") {
      value = "-";
    }

    const cellKey = `${rowIndex}-${column.key}`;
    const isCurrentlyEditing = editingCells.has(cellKey);
    const hasLongText = isTextLong(value);
    const isVeryLongText = isTextVeryLong(value);
    const showTooltip = shouldShowTooltip(value);

    let displayText, fullText, hasMore;
    if (isVeryLongText) {
      const formatted = formatTextForDisplay(value);
      displayText = formatted.displayText;
      fullText = formatted.fullText;
      hasMore = formatted.hasMore;
    } else if (hasLongText) {
      displayText = truncateText(value);
      fullText = value;
      hasMore = false;
    } else {
      displayText = value;
      fullText = value;
      hasMore = false;
    }

    if (isEditable && isCurrentlyEditing && control) {
      return (
        <Controller
          name={fieldName}
          control={control}
          defaultValue={value === "-" ? "" : value || ""}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              disabled={!isEditingEnabled}
              multiline={column.multiline || false}
              minRows={column.multiline ? 2 : 1}
              maxRows={column.multiline ? 8 : 1}
              placeholder={`Enter ${column.label}...`}
              onBlur={(e) => {
                field.onBlur(e);
                handleCellBlur(rowIndex, column.key);
                if (onFieldBlur) {
                  onFieldBlur(rowIndex, column.key, e.target.value);
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                  minHeight: column.multiline ? "auto" : "40px",
                  backgroundColor: "#fff",
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                  },
                },
              }}
            />
          )}
        />
      );
    }

    const isCalculatedField =
      autoCalculation && column.key === autoCalculation.targetField;
    const hasCalculatedValue = calculatedValues[fieldName] !== undefined;

    const cellContent = (
      <span>
        {value === "-" && isEditable ? (
          <span className="text-gray-400 italic">
            Click to enter {column.label}
          </span>
        ) : isVeryLongText ? (
          <div className="relative">
            <div
              className="whitespace-pre-line text-sm leading-tight"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: "1.2",
                maxHeight: "3.6em",
              }}
            >
              {displayText}
            </div>
            {hasMore && <span className="text-gray-500 text-xs">...</span>}
          </div>
        ) : (
          displayText
        )}
      </span>
    );

    const wrappedContent = showTooltip ? (
      <Tooltip
        title={fullText}
        placement="top"
        arrow
        PopperProps={{
          sx: {
            "& .MuiTooltip-tooltip": {
              fontSize: "13px",
              maxWidth: "400px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              backgroundColor: "#333",
              color: "#fff",
              padding: "8px 12px",
              borderRadius: "6px",
            },
            "& .MuiTooltip-arrow": {
              color: "#333",
            },
          },
        }}
      >
        {cellContent}
      </Tooltip>
    ) : (
      cellContent
    );

    const commonStyle = {
      ...column.cellStyle,
      cursor: isEditable ? "pointer" : "default",
    };

    if (isEditable) {
      return (
        <button
          type="button"
          className={`w-full text-left ${
            !disableCellHover ? "hover:bg-gray-50" : ""
          } rounded transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600`}
          onClick={() => handleCellClick(rowIndex, column.key)}
          style={{
            ...commonStyle,
            padding: "4px 0",
            margin: 0,
          }}
        >
          {wrappedContent}
        </button>
      );
    }

    if (isCalculatedField && hasCalculatedValue) {
      return (
        <div
          className={`${
            column.cellClassName || ""
          } font-semibold text-blue-700 transition-all duration-200`}
          style={{
            ...commonStyle,
            padding: "4px 0",
            margin: 0,
          }}
        >
          {wrappedContent}
        </div>
      );
    }

    return (
      <div
        className={`${column.cellClassName || ""} transition-all duration-200`}
        style={{
          ...commonStyle,
          padding: "4px 0",
          margin: 0,
        }}
      >
        {wrappedContent}
      </div>
    );
  };

  const isAllSelected =
    enableSelection &&
    selectedItems &&
    selectedItems.length === data.length &&
    data.length > 0;
  const isIndeterminate =
    enableSelection &&
    selectedItems &&
    selectedItems.length > 0 &&
    selectedItems.length < data.length;

  const handleSelectAllChange = (checked) => {
    if (onSelectAll) {
      onSelectAll(checked);
    }
  };

  const handleItemSelectionChange = (itemKey, checked) => {
    if (onSelectionChange) {
      onSelectionChange(itemKey, checked);
    }
  };

  const handleRowClick = (row) => {
    if (onRowClick && row.id) {
      onRowClick(row.id);
    }
  };

  const getSizeStyles = () => {
    if (height || maxHeight) {
      return {
        height: height || "auto",
        maxHeight: maxHeight || "none",
        overflow: "auto",
      };
    }

    switch (size) {
      case "fixed":
        return {
          height: "384px",
          overflow: "auto",
        };
      case "small":
        return {
          height: "200px",
          overflow: "auto",
        };
      case "medium":
        return {
          height: "300px",
          overflow: "auto",
        };
      case "large":
        return {
          height: "500px",
          overflow: "auto",
        };
      case "auto":
      default:
        return {
          height: "calc(100vh - 200px)",
          overflow: "auto",
        };
    }
  };

  const handlePageChange = (page) => {
    if (onPageChange && pagination) {
      onPageChange(page);
    }
  };

  return (
    <div className="w-full">
      <CustomTableContainer
        component={Paper}
        className={`${containerClassName}`}
        sx={getSizeStyles()}
      >
        <Table
          stickyHeader
          className={tableClassName}
          sx={{
            "& .MuiTableCell-root": {
              borderLeft: "none",
              borderRight: "none",
              padding: "12px 12px",
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              borderLeft: "none",
              borderRight: "none",
              padding: "12px 12px",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {enableSelection && showSelectAllInHeader && (
                <StyledHeaderCell className={headerClassName}>
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                    size="small"
                    inputProps={{ "aria-label": "Select all rows" }}
                  />
                </StyledHeaderCell>
              )}
              {enableSelection && !showSelectAllInHeader && (
                <StyledHeaderCell
                  className={headerClassName}
                ></StyledHeaderCell>
              )}
              {columns.map((column, index) => (
                <StyledHeaderCell
                  key={column.key || index}
                  className={`${headerClassName} ${
                    column.headerClassName || ""
                  }`}
                  style={column.headerStyle}
                >
                  {column.label}
                </StyledHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell
                  colSpan={columns.length + (enableSelection ? 1 : 0)}
                  className="text-center py-12"
                >
                  <div className="flex flex-col items-center justify-center text-gray-500">
                    {emptyStateIcon || (
                      <svg
                        className="w-12 h-12 mx-auto mb-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                    )}
                    <p className="font-medium">{emptyStateMessage}</p>
                  </div>
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              data.map((row, rowIndex) => {
                const TableRowComponent = disableRowHover
                  ? StyledTableRowNoHover
                  : StyledTableRow;
                return (
                  <TableRowComponent
                    key={row.id || rowIndex}
                    className={`${rowClassName} ${row.className || ""}`}
                    {...(onRowClick && {
                      onClick: () => handleRowClick(row),
                      onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleRowClick(row);
                        }
                      },
                      tabIndex: 0,
                      role: "button",
                      "aria-label": `Open row ${row.id || rowIndex + 1}`,
                    })}
                  >
                    {enableSelection && (
                      <StyledTableCell className={cellClassName}>
                        <Checkbox
                          checked={
                            selectedItems &&
                            selectedItems.includes(row[selectionKey])
                          }
                          onChange={(e) =>
                            handleItemSelectionChange(
                              row[selectionKey],
                              e.target.checked
                            )
                          }
                          size="small"
                          inputProps={{
                            "aria-label": `Select row ${row.id || rowIndex + 1}`,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </StyledTableCell>
                    )}
                    {columns.map((column, colIndex) => (
                      <StyledTableCell
                        key={`${rowIndex}-${colIndex}-${column.key}`}
                        className={`${cellClassName} ${
                          column.cellClassName || ""
                        }`}
                        style={{
                          ...column.cellStyle,
                          textAlign: column.headerStyle?.textAlign || "left",
                        }}
                      >
                        {renderCell(row, column, rowIndex)}
                      </StyledTableCell>
                    ))}
                  </TableRowComponent>
                );
              })
            )}
          </TableBody>
        </Table>
      </CustomTableContainer>

      {/* Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-center mt-6 space-x-4">
          {/* Previous Page Button */}
          <button
            onClick={() => handlePageChange(pagination.previous_page)}
            disabled={!pagination.has_previous_page || paginationLoading}
            className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              pagination.has_previous_page && !paginationLoading
                ? "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                : "text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed"
            }`}
          >
            {paginationLoading ? (
              <svg
                className="w-4 h-4 mr-1 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            )}
            Previous
          </button>

          {/* Page Numbers */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">
              Page {pagination.current_page} of {pagination.total_pages}
            </span>
            {paginationLoading && (
              <span className="text-xs text-gray-500">Loading...</span>
            )}
          </div>

          {/* Next Page Button */}
          <button
            onClick={() => handlePageChange(pagination.next_page)}
            disabled={!pagination.has_next_page || paginationLoading}
            className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              pagination.has_next_page && !paginationLoading
                ? "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                : "text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed"
            }`}
          >
            Next
            {paginationLoading ? (
              <svg
                className="w-4 h-4 ml-1 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <svg
                className="w-4 h-4 ml-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

const CustomTableContainer = styled(TableContainer)(() => ({
  borderRadius: "12px",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  minWidth: "100%",
}));

const StyledTableRow = styled(TableRow)(({ onClick }) => ({
  border: "none",
  "&:hover": {
    backgroundColor: "#f8f9fa",
  },
  ...(onClick && {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f1f5f9",
    },
  }),
}));

const StyledTableRowNoHover = styled(TableRow)(() => ({
  border: "none",
}));

const StyledTableCell = styled(TableCell)(() => ({
  fontSize: "14px",
  padding: "12px 12px",
  borderBottom: "1px solid #f0f0f0",
  color: "#181d27",
  maxWidth: "300px",
  verticalAlign: "middle",
  textAlign: "left",
  margin: 0,
  borderLeft: "none",
  borderRight: "none",
  "& > *": {
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
  },
}));

const StyledHeaderCell = styled(TableCell)({
  fontWeight: "600",
  fontSize: "14px",
  backgroundColor: "#F1F5FF",
  color: "#414651",
  padding: "12px 12px",
  whiteSpace: "normal",
  wordWrap: "break-word",
  position: "sticky",
  top: 0,
  zIndex: 1,
  textAlign: "left",
  verticalAlign: "middle",
  lineHeight: "1.3",
  minWidth: "120px",
  maxWidth: "200px",
  margin: 0,
  borderLeft: "none",
  borderRight: "none",
});

export default ReusableTable;
